// Personal purchase: Recipient → Bundle → Review & pay, then the order's own
// Status screen (route everyday-services/orders/:id).
//
// Money-safety rules this component keeps:
//  - The network is CHOSEN by the buyer, never inferred from a phone prefix.
//  - Prices and provider codes come from the server catalogue; the browser only
//    sends the offer id it reviewed. A changed price sends the buyer back to
//    review it (quote_changed).
//  - The paying number is confirmed for every purchase. A saved number is a
//    masked suggestion; the wallet's own PIN prompt is the authorization and
//    KaziPeople never asks for the PIN.
//  - One Idempotency-Key per attempt, kept across refresh (sessionStorage). An
//    uncertain outcome is never "pay again": the buyer is told to check first,
//    and nothing is re-sent automatically.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ArrowLeft, Check, Loader2, Smartphone, Trash2, User, Users } from 'lucide-react';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Input } from '@/features/shared/components/ui/input';
import { Label } from '@/features/shared/components/ui/label';
import { Checkbox } from '@/features/shared/components/ui/checkbox';
import { DataState, errorVariant } from '@/features/shared/components/ui/DataState';
import { Skeleton } from '@/features/shared/components/ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/features/shared/components/ui/select';
import { PhoneInput } from '@/features/shared/components/PhoneInput';
import { MoneyInput } from '@/features/shared/components/MoneyInput';
import { useAppSelector } from '@/app/hooks';
import { formatMoney } from '@/lib/money';
import { getApiErrorMessage } from '@/lib/apiError';
import { cn } from '@/lib/utils';
import { commerceApi } from '../../api/commerceApi';
import {
  attemptFor, clearAttempt, keepAttemptAfterError, loadAttempt, saveAttempt, type PurchaseAttempt,
} from '../../lib/purchaseAttempt';
import { isCompletePhone, operatorName, phoneDigits, toE164 } from '../../lib/format';
import type {
  Availability, Catalogue, CreateOrderInput, Offer, Order, ProductType, SavedPayer, SavedRecipient,
} from '../../types';
import { ProductIcon } from '../PurchasesList';
import { PurchaseSummary, type SummaryModel } from './PurchaseSummary';

const COUNTRY = 'TZ';
// This slice sells airtime and bundles. LUKU stays out until meter validation
// and token recovery are proven with the provider.
const CHECKOUT_PRODUCTS: ProductType[] = ['AIRTIME', 'DATA_BUNDLE'];

export type Step = 'recipient' | 'bundle' | 'review';

// A provider that needs no product code (no mapped offers) still sells an open
// amount within the product's limits; the order then carries no offer id.
const OPEN_AMOUNT_ID = '__open__';
const STEPS: Step[] = ['recipient', 'bundle', 'review'];

function isQuoteChanged(err: unknown): boolean {
  const r = (err as { response?: { status?: number; data?: any } })?.response;
  const msg = String(r?.data?.message ?? r?.data?.error ?? '');
  return r?.status === 409 && msg.includes('quote_changed');
}

export const StepTabs: React.FC<{ current: Step | 'status'; onGo?: (s: Step) => void; reachable?: (s: Step) => boolean }> = ({ current, onGo, reachable }) => {
  const { t } = useTranslation('everydayServices');
  const all: (Step | 'status')[] = [...STEPS, 'status'];
  return (
    <nav aria-label={t('steps.label')}>
      <ol className="flex flex-wrap gap-x-5 gap-y-2 text-sm">
        {all.map((s, i) => {
          const active = s === current;
          const can = s !== 'status' && !active && !!onGo && (reachable?.(s) ?? false);
          const label = `${i + 1} · ${t(`steps.${s}`)}`;
          return (
            <li key={s}>
              {can ? (
                <button type="button" onClick={() => onGo?.(s as Step)}
                  className="rounded text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                  {label}
                </button>
              ) : (
                <span aria-current={active ? 'step' : undefined}
                  className={cn('pb-1', active ? 'border-b-2 border-primary-500 font-semibold text-foreground' : 'text-muted-foreground')}>
                  {label}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

const Choice: React.FC<{ selected: boolean; onClick: () => void; children: React.ReactNode; disabled?: boolean; describedBy?: string }> = ({ selected, onClick, children, disabled, describedBy }) => (
  <button
    type="button"
    role="radio"
    aria-checked={selected}
    aria-describedby={describedBy}
    disabled={disabled}
    onClick={onClick}
    className={cn(
      'flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50',
      selected ? 'border-primary-500 bg-accent text-accent-foreground' : 'border-border bg-card text-foreground hover:bg-muted',
    )}
  >
    {children}
  </button>
);

interface Props {
  availability: Availability;
  onPlaced: (order: Order) => void;
  onOpenPurchases: () => void;
}

export const PersonalCheckout: React.FC<Props> = ({ availability, onPlaced, onOpenPurchases }) => {
  const { t } = useTranslation('everydayServices');
  const myPhone = useAppSelector((s) => toE164(s.auth?.user?.phone));

  const products = useMemo(
    () => availability.products.filter((p) => CHECKOUT_PRODUCTS.includes(p.productType)),
    [availability.products],
  );
  const [step, setStep] = useState<Step>('recipient');
  const [productType, setProductType] = useState<ProductType>(products[0]?.productType ?? 'AIRTIME');
  const product = products.find((p) => p.productType === productType);

  // --- recipient ---
  const [relation, setRelation] = useState<'self' | 'other'>('self');
  const [recipient, setRecipient] = useState<string>(myPhone);
  const [label, setLabel] = useState('');
  const [operator, setOperator] = useState('');
  const [saved, setSaved] = useState<SavedRecipient[]>([]);
  const [savedId, setSavedId] = useState<string>('');
  const [saveIt, setSaveIt] = useState(false);
  const [recipientErrors, setRecipientErrors] = useState(false);

  useEffect(() => {
    commerceApi.listRecipients().then(setSaved).catch(() => setSaved([]));
  }, []);

  const pickRelation = (r: 'self' | 'other') => {
    setRelation(r);
    setSavedId('');
    setRecipient(r === 'self' ? myPhone : '');
    setLabel('');
  };
  const pickSaved = (id: string) => {
    setSavedId(id);
    const s = saved.find((x) => x.id === id);
    if (s) { setOperator(s.operator); setLabel(s.label); }
  };
  const forget = async (id: string) => {
    try {
      await commerceApi.deleteRecipient(id);
      setSaved((l) => l.filter((x) => x.id !== id));
      if (savedId === id) setSavedId('');
    } catch { /* stays listed; the buyer can retry */ }
  };

  const recipientDigits = phoneDigits(recipient);
  const savedPerson = saved.find((x) => x.id === savedId);
  const recipientOk = savedPerson ? true : isCompletePhone(recipientDigits);
  const recipientValid = recipientOk && !!operator;

  // --- bundle ---
  const [catalogue, setCatalogue] = useState<Catalogue | null>(null);
  const [catError, setCatError] = useState<unknown>(null);
  const [offerId, setOfferId] = useState('');
  const [amount, setAmount] = useState('');
  const [bundleErrors, setBundleErrors] = useState(false);

  const loadCatalogue = useCallback(async () => {
    setCatalogue(null);
    setCatError(null);
    try {
      setCatalogue(await commerceApi.catalogue(productType, operator, COUNTRY));
    } catch (e) {
      setCatError(e);
    }
  }, [productType, operator]);

  const hasLimits = catalogue?.minAmount != null || catalogue?.maxAmount != null;
  const openOffer: Offer | undefined = catalogue?.offers.find((o) => o.price == null)
    ?? (catalogue && catalogue.offers.length === 0 && hasLimits
      ? { id: OPEN_AMOUNT_ID, name: '', currency: catalogue.currency, validity: '', volume: '' } : undefined);
  const fixedOffers = catalogue?.offers.filter((o) => o.price != null) ?? [];
  const chosen: Offer | undefined = offerId === OPEN_AMOUNT_ID ? openOffer : catalogue?.offers.find((o) => o.id === offerId);
  const currency = catalogue?.currency || product?.currency || 'TZS';
  const money = (n: number) => formatMoney(n, currency);
  const minA = catalogue?.minAmount ?? product?.minAmount;
  const maxA = catalogue?.maxAmount ?? product?.maxAmount;
  const openAmount = Number(amount);
  const isOpen = !!chosen && chosen.price == null;
  const price = chosen ? (chosen.price ?? (Number.isFinite(openAmount) && openAmount > 0 ? Math.round(openAmount) : undefined)) : undefined;
  const amountError = isOpen && (!(openAmount > 0)
    ? t('form.errors.amount')
    : minA != null && openAmount < minA ? t('form.errors.min', { min: money(minA) })
      : maxA != null && openAmount > maxA ? t('form.errors.max', { max: money(maxA) }) : undefined);
  const bundleValid = !!chosen && !amountError && price != null;

  // --- payer ---
  const [payers, setPayers] = useState<SavedPayer[] | null>(null);
  const [payerChoice, setPayerChoice] = useState<string>('other'); // method id or 'other'
  const [payerPhone, setPayerPhone] = useState<string>(myPhone);
  const [payerConfirmed, setPayerConfirmed] = useState(false);
  const [reviewErrors, setReviewErrors] = useState(false);
  const payerDigits = phoneDigits(payerPhone);
  const chosenPayer = payers?.find((p) => p.methodId === payerChoice && p.eligible);
  const payerDisplay = chosenPayer ? chosenPayer.masked : (isCompletePhone(payerDigits) ? `+${payerDigits}` : '');
  const payerValid = (!!chosenPayer || isCompletePhone(payerDigits)) && payerConfirmed;

  useEffect(() => {
    if (step !== 'review') return;
    setPayers(null);
    commerceApi.payerSuggestion(productType, operator, COUNTRY)
      .then((list) => {
        setPayers(list);
        const first = list.find((p) => p.eligible);
        setPayerChoice(first ? first.methodId : 'other');
      })
      .catch(() => { setPayers([]); setPayerChoice('other'); });
  }, [step, productType, operator]);

  // Any change to WHO pays or WHAT is bought needs a fresh confirmation.
  useEffect(() => { setPayerConfirmed(false); }, [payerChoice, payerPhone, offerId, amount, operator, recipient, savedId]);

  // --- submit ---
  const attemptRef = useRef<PurchaseAttempt | null>(null);
  const [pending] = useState(() => loadAttempt());
  const [showPendingNotice, setShowPendingNotice] = useState(!!pending);
  const [submitting, setSubmitting] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [submitError, setSubmitError] = useState<string>('');

  const input: CreateOrderInput | null = chosen && price != null ? {
    countryCode: COUNTRY,
    productType,
    operator,
    recipient: savedPerson ? '' : recipientDigits,
    savedRecipientId: savedPerson?.id,
    recipientRelation: savedPerson ? 'other' : relation,
    recipientLabel: relation === 'other' ? (savedPerson?.label ?? label.trim()) : undefined,
    saveRecipient: relation === 'other' && !savedPerson && saveIt,
    payerMsisdn: chosenPayer ? '' : payerDigits,
    payerMethodId: chosenPayer?.methodId,
    payerConfirmed,
    offerId: chosen.id === OPEN_AMOUNT_ID ? undefined : chosen.id,
    amount: price,
    currency,
  } : null;

  const submit = async () => {
    if (submitting) return;
    if (!input || !payerValid) { setReviewErrors(true); return; }
    // Same purchase after a refresh → the stored attempt's key (never a new charge).
    const prior = attemptRef.current ?? (pending ? { key: pending.key, fingerprint: pending.fingerprint } : null);
    const attempt = attemptFor(prior, input);
    attemptRef.current = attempt;
    saveAttempt(attempt);
    setSubmitting(true);
    setSubmitError('');
    try {
      const order = await commerceApi.createOrder(input, attempt.key);
      attemptRef.current = null;
      clearAttempt();
      setUncertain(false);
      onPlaced(order);
    } catch (err) {
      if (keepAttemptAfterError(err)) {
        // The server may have it. Never suggest paying again.
        setUncertain(true);
      } else {
        attemptRef.current = null;
        clearAttempt();
        if (isQuoteChanged(err)) {
          setSubmitError(t('review.quoteChanged'));
          setOfferId('');
          setStep('bundle');
          void loadCatalogue();
        } else {
          setSubmitError(getApiErrorMessage(err, t('errors.placeOrder')));
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  const goBundle = () => {
    if (!recipientValid) { setRecipientErrors(true); return; }
    setStep('bundle');
    setOfferId('');
    void loadCatalogue();
  };
  const goReview = () => {
    if (!bundleValid) { setBundleErrors(true); return; }
    setStep('review');
  };
  const reachable = (s: Step) => s === 'recipient' || (s === 'bundle' && recipientValid) || (s === 'review' && recipientValid && bundleValid);

  const net = operatorName(operator, availability.operators);
  const summary: SummaryModel = {
    context: 'personal',
    recipientName: relation === 'self' && !savedPerson ? t('recipient.myself') : (savedPerson?.label || label.trim() || undefined),
    recipientNumber: savedPerson ? savedPerson.recipient : (recipientDigits ? `+${recipientDigits}` : undefined),
    network: net || undefined,
    product: chosen ? (chosen.price == null ? t(`products.${productType}`) : (chosen.name || chosen.volume)) : undefined,
    validity: chosen?.validity || undefined,
    price,
    fee: 0,
    currency,
    payer: payerDisplay || undefined,
  };

  const fieldError = (show: boolean, msg?: string | false) => (show && msg
    ? <p className="mt-1 text-xs text-danger-strong" role="alert">{msg}</p> : null);

  return (
    <div className="space-y-5">
      {showPendingNotice && (
        <div role="status" className="flex flex-col gap-3 rounded-xl border border-warning-soft bg-warning-soft p-4 text-sm text-warning-strong sm:flex-row sm:items-center sm:justify-between">
          <p className="flex gap-2"><AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />{t('pending.message')}</p>
          <div className="flex shrink-0 gap-2">
            <Button size="sm" variant="outline" onClick={onOpenPurchases}>{t('pending.check')}</Button>
            <Button size="sm" variant="ghost" onClick={() => setShowPendingNotice(false)}>{t('pending.dismiss')}</Button>
          </div>
        </div>
      )}

      {/* Once the outcome is uncertain the purchase is locked: an edit would be a
          new purchase (new key) and could charge twice. */}
      <StepTabs current={step} onGo={uncertain ? undefined : setStep} reachable={reachable} />

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start">
        <Card>
          <CardContent className="space-y-5 p-5 sm:p-6">
            {step === 'recipient' && (
              <section aria-labelledby="es-recipient-h" className="space-y-5">
                {products.length > 1 && (
                  <div role="radiogroup" aria-label={t('recipient.service')} className="flex flex-wrap gap-2">
                    {products.map((p) => (
                      <Choice key={p.productType} selected={productType === p.productType}
                        onClick={() => { setProductType(p.productType); setOfferId(''); }}>
                        <ProductIcon type={p.productType} />{t(`products.${p.productType}`)}
                      </Choice>
                    ))}
                  </div>
                )}
                <h2 id="es-recipient-h" className="text-lg font-semibold text-foreground">{t('recipient.title')}</h2>
                <div role="radiogroup" aria-label={t('recipient.title')} className="flex flex-wrap gap-2">
                  <Choice selected={relation === 'self'} onClick={() => pickRelation('self')}><User className="h-4 w-4" aria-hidden="true" />{t('recipient.myself')}</Choice>
                  <Choice selected={relation === 'other'} onClick={() => pickRelation('other')}><Users className="h-4 w-4" aria-hidden="true" />{t('recipient.someoneElse')}</Choice>
                </div>

                {relation === 'other' && saved.length > 0 && (
                  <div>
                    <p className="mb-2 text-sm font-medium text-foreground">{t('recipient.saved')}</p>
                    <ul className="flex flex-wrap gap-2">
                      {saved.map((s) => (
                        <li key={s.id} className="flex items-center gap-1">
                          <Choice selected={savedId === s.id} onClick={() => pickSaved(s.id)}>
                            {s.label || s.recipient}<span className="text-xs text-muted-foreground">{s.recipient}</span>
                          </Choice>
                          <Button type="button" size="sm" variant="ghost" onClick={() => forget(s.id)} aria-label={t('recipient.forget', { name: s.label || s.recipient })}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </li>
                      ))}
                    </ul>
                    {savedId && (
                      <Button type="button" variant="link" size="sm" className="h-auto p-0" onClick={() => setSavedId('')}>{t('recipient.newPerson')}</Button>
                    )}
                  </div>
                )}

                {relation === 'other' && !savedPerson && (
                  <div>
                    <Label htmlFor="es-label" className="mb-1.5 block">{t('recipient.name')} <span className="font-normal text-muted-foreground">({t('recipient.optional')})</span></Label>
                    <Input id="es-label" value={label} maxLength={40} onChange={(e) => setLabel(e.target.value)} placeholder={t('recipient.namePlaceholder')} />
                  </div>
                )}

                {!savedPerson && (
                  <div>
                    <Label htmlFor="es-recipient" className="mb-1.5 block">{relation === 'self' ? t('recipient.myNumber') : t('recipient.theirNumber')}</Label>
                    <PhoneInput id="es-recipient" value={recipient} onChange={setRecipient} />
                    <p className="mt-1 text-xs text-muted-foreground">{t('recipient.numberHint')}</p>
                    {fieldError(recipientErrors, !recipientOk && t('form.errors.phone'))}
                  </div>
                )}

                <div>
                  <Label htmlFor="es-network" className="mb-1.5 block">{relation === 'self' ? t('recipient.myNetwork') : t('recipient.theirNetwork')}</Label>
                  <Select value={operator} onValueChange={(v) => { setOperator(v); setOfferId(''); }}>
                    <SelectTrigger id="es-network" aria-invalid={recipientErrors && !operator}><SelectValue placeholder={t('form.networkPlaceholder')} /></SelectTrigger>
                    <SelectContent>
                      {availability.operators.map((o) => <SelectItem key={o.code} value={o.code}>{o.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="mt-1 text-xs text-muted-foreground">{t('recipient.networkHint')}</p>
                  {fieldError(recipientErrors, !operator && t('form.errors.network'))}
                </div>

                {relation === 'other' && (
                  <>
                    {!savedPerson && (
                      <label className="flex items-start gap-2 text-sm text-foreground">
                        <Checkbox checked={saveIt} onCheckedChange={(v) => setSaveIt(v === true)} aria-describedby="es-save-hint" />
                        <span>{t('recipient.save')}<span id="es-save-hint" className="block text-xs text-muted-foreground">{t('recipient.saveHint')}</span></span>
                      </label>
                    )}
                    <p className="rounded-lg bg-muted p-3 text-xs text-muted-foreground">{t('recipient.noAccountNeeded')}</p>
                  </>
                )}

                <div className="flex justify-end">
                  <Button type="button" onClick={goBundle} className="w-full sm:w-auto">{t('recipient.next')}</Button>
                </div>
              </section>
            )}

            {step === 'bundle' && (
              <section aria-labelledby="es-bundle-h" className="space-y-5">
                <h2 id="es-bundle-h" className="text-lg font-semibold text-foreground">{t(`bundle.title.${productType}`)}</h2>
                {submitError && <p role="alert" className="rounded-lg bg-warning-soft p-3 text-sm text-warning-strong">{submitError}</p>}
                {catError ? (
                  errorVariant(catError) === 'error' && (catError as any)?.response?.status !== 403
                    ? <DataState variant="error" title={t('bundle.loadError')} message={getApiErrorMessage(catError, t('bundle.loadError'))} onRetry={loadCatalogue} />
                    : <DataState variant="unavailable" title={t('bundle.unavailableTitle')} message={t('bundle.unavailable', { network: net })} />
                ) : !catalogue ? (
                  <div className="space-y-2" aria-busy="true" aria-label={t('bundle.loading')}>
                    <Skeleton className="h-14 w-full" /><Skeleton className="h-14 w-full" /><Skeleton className="h-14 w-full" />
                  </div>
                ) : catalogue.offers.length === 0 && !openOffer ? (
                  <DataState variant="empty" title={t('bundle.emptyTitle')} message={t('bundle.empty', { network: net })} />
                ) : (
                  <div className="space-y-4">
                    {fixedOffers.length > 0 && (
                      <div role="radiogroup" aria-label={t(`bundle.title.${productType}`)} className="grid gap-2 sm:grid-cols-2">
                        {fixedOffers.map((o) => (
                          <button key={o.id} type="button" role="radio" aria-checked={offerId === o.id} onClick={() => setOfferId(o.id)}
                            className={cn('flex items-center justify-between gap-3 rounded-xl border p-4 text-left text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                              offerId === o.id ? 'border-primary-500 bg-accent' : 'border-border bg-card hover:bg-muted')}>
                            <span>
                              <span className="block font-semibold text-foreground">{o.name || o.volume}</span>
                              {o.validity && <span className="block text-xs text-muted-foreground">{t('bundle.valid', { validity: o.validity })}</span>}
                            </span>
                            <span className="font-semibold tabular-nums text-foreground">{money(o.price!)}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    {openOffer && (
                      <div className={cn('rounded-xl border p-4', offerId === openOffer.id ? 'border-primary-500' : 'border-border')}>
                        <label className="flex items-center gap-2 text-sm font-medium text-foreground">
                          <input type="radio" name="es-offer" className="accent-[hsl(var(--primary))]" checked={offerId === openOffer.id} onChange={() => setOfferId(openOffer.id)} />
                          {t('bundle.chooseAmount')}
                        </label>
                        {offerId === openOffer.id && (
                          <div className="mt-3">
                            <Label htmlFor="es-amount" className="sr-only">{t('form.amount')}</Label>
                            <MoneyInput id="es-amount" value={amount} onChange={setAmount} currency={currency}
                              aria-invalid={bundleErrors && !!amountError} aria-describedby="es-amount-hint" />
                            {minA != null && maxA != null && (
                              <p id="es-amount-hint" className="mt-1 text-xs text-muted-foreground">{t('form.amountBetween', { min: money(minA), max: money(maxA) })}</p>
                            )}
                            {fieldError(bundleErrors, amountError)}
                          </div>
                        )}
                      </div>
                    )}
                    {fieldError(bundleErrors, !chosen && t('bundle.pick'))}
                  </div>
                )}
                <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
                  <Button type="button" variant="ghost" onClick={() => setStep('recipient')}><ArrowLeft className="h-4 w-4" />{t('steps.back')}</Button>
                  <Button type="button" onClick={goReview} disabled={!catalogue || (catalogue.offers.length === 0 && !openOffer)}>{t('bundle.next')}</Button>
                </div>
              </section>
            )}

            {step === 'review' && (
              <section aria-labelledby="es-review-h" className="space-y-5">
                <h2 id="es-review-h" className="text-lg font-semibold text-foreground">{t('review.title')}</h2>

                <div className="rounded-xl border border-border p-4 text-sm">
                  <p className="text-muted-foreground">{t('review.recipientGets')}</p>
                  <p className="mt-1 font-semibold text-foreground">
                    {summary.product}{summary.validity ? ` · ${summary.validity}` : ''} → {summary.recipientName ? `${summary.recipientName} (${summary.recipientNumber})` : summary.recipientNumber}
                  </p>
                  <p className="text-muted-foreground">{net}</p>
                </div>

                <fieldset className="space-y-3" disabled={uncertain || submitting}>
                  <legend className="mb-1 text-sm font-medium text-foreground">{t('review.payWith')}</legend>
                  {payers === null ? <Skeleton className="h-12 w-full" /> : (
                    <div role="radiogroup" aria-label={t('review.payWith')} className="space-y-2">
                      {payers.map((p) => (
                        <label key={p.methodId} className={cn('flex items-center justify-between gap-3 rounded-xl border p-3 text-sm',
                          !p.eligible && 'opacity-60', payerChoice === p.methodId ? 'border-primary-500 bg-accent' : 'border-border')}>
                          <span className="flex items-center gap-2">
                            <input type="radio" name="es-payer" disabled={!p.eligible} checked={payerChoice === p.methodId}
                              onChange={() => setPayerChoice(p.methodId)} className="accent-[hsl(var(--primary))]" />
                            <Smartphone className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                            <span><span className="font-medium text-foreground tabular-nums">{p.masked}</span> <span className="text-muted-foreground">{p.provider}</span>
                              <span className="block text-xs text-muted-foreground">{p.eligible ? t('review.savedNumber') : t(`review.ineligible.${p.reason || 'unavailable'}`, t('review.ineligible.unavailable'))}</span>
                            </span>
                          </span>
                        </label>
                      ))}
                      <label className={cn('block rounded-xl border p-3 text-sm', payerChoice === 'other' ? 'border-primary-500 bg-accent' : 'border-border')}>
                        <span className="flex items-center gap-2">
                          <input type="radio" name="es-payer" checked={payerChoice === 'other'} onChange={() => setPayerChoice('other')} className="accent-[hsl(var(--primary))]" />
                          <span className="font-medium text-foreground">{payers.length ? t('review.anotherNumber') : t('review.payingNumber')}</span>
                        </span>
                        {payerChoice === 'other' && (
                          <div className="mt-3">
                            <PhoneInput id="es-payer" value={payerPhone} onChange={setPayerPhone} />
                            {fieldError(reviewErrors, !isCompletePhone(payerDigits) && t('form.errors.wallet'))}
                          </div>
                        )}
                      </label>
                    </div>
                  )}
                </fieldset>

                <label className="flex items-start gap-2 rounded-xl bg-muted p-3 text-sm text-foreground">
                  <Checkbox checked={payerConfirmed} onCheckedChange={(v) => setPayerConfirmed(v === true)}
                    disabled={!payerDisplay || price == null || uncertain || submitting} aria-invalid={reviewErrors && !payerConfirmed} />
                  <span>{payerDisplay && price != null
                    ? t('review.confirm', { payer: payerDisplay, total: money(price) })
                    : t('review.confirmPending')}</span>
                </label>
                {fieldError(reviewErrors, !payerConfirmed && t('review.confirmRequired'))}

                <p className="text-xs text-muted-foreground">{t('review.pinNote')}</p>

                {uncertain && (
                  <div role="alert" className="rounded-xl bg-warning-soft p-4 text-sm text-warning-strong">
                    <p className="font-semibold">{t('review.uncertainTitle')}</p>
                    <p className="mt-1">{t('review.uncertain')}</p>
                    <Button size="sm" variant="outline" className="mt-3" onClick={onOpenPurchases}>{t('pending.check')}</Button>
                  </div>
                )}
                {submitError && step === 'review' && <p role="alert" className="text-sm text-danger-strong">{submitError}</p>}

                <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
                  <Button type="button" variant="ghost" onClick={() => setStep('bundle')} disabled={submitting || uncertain}><ArrowLeft className="h-4 w-4" />{t('steps.back')}</Button>
                  <Button type="button" onClick={submit} disabled={submitting}>
                    {submitting
                      ? <><Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />{t('form.placing')}</>
                      : uncertain ? <>{t('review.retrySafe')}</> : <><Check className="h-4 w-4" aria-hidden="true" />{t('review.continue')}</>}
                  </Button>
                </div>
                <p className="sr-only" aria-live="polite">{submitting ? t('form.placing') : ''}</p>
              </section>
            )}
          </CardContent>
        </Card>

        <PurchaseSummary model={summary} />
      </div>
    </div>
  );
};

export default PersonalCheckout;
